// This import adds IE9 and above polyfill. Further language features can be supported if required see:
// https://github.com/facebook/create-react-app/blob/master/packages/react-app-polyfill/README.md
import "react-app-polyfill/ie9";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
// Redux state
import { Provider } from "react-redux";
import { store } from "./State/store";
// Components & styles
import App from "./App";
import LoadingHOC from "./Components/LoadingHOC";
import { ViewportProvider } from "./Components/ViewportProvider";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import theme from "./Styles/material-ui-theme";
import "./Styles/reset.scss"; // This allows all default styling to be reset and actively managed thereafter.
import "./Styles/base.scss";
import ScrollToTop from "./Components/ScrollToTop";
import * as amplitude from "@amplitude/analytics-browser";
import ErrorFallback from "Pages/ErrorFallback";
import { LogLevel } from "@amplitude/analytics-browser/lib/esm/types";

const initialiseAmplitude = ({
  testEnvironment,
}: {
  testEnvironment: boolean;
}) => {
  amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, {
    serverZone: "EU",
    autocapture: false,
    logLevel: testEnvironment ? LogLevel.Error : LogLevel.None,
  });
};
if (
  process.env.NODE_ENV === "production" &&
  /^https:\/\/apps-development-(1[0-5]|[1-9])\.intelligentlilli\.com$/.test(
    window.location.origin
  )
) {
  initialiseAmplitude({ testEnvironment: true });
}

if (
  process.env.NODE_ENV === "production" &&
  window.location.origin === "https://app.intelligentlilli.com"
) {
  initialiseAmplitude({ testEnvironment: false });

  Sentry.init({
    dsn: "https://effd6ce625ae4b31af8cc93f91e11e3a@o506894.ingest.sentry.io/5597206",
    // TO DO: Browser tracing does not seem to have been set up, see docs here:
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/performance/instrumentation/automatic-instrumentation/
    integrations: [Sentry.browserTracingIntegration()],
    environment: process.env.NODE_ENV,
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
  });
}

// Hack to allow us to host on S3 without having fully-fledged SSR on Lambda or something
// https://via.studio/journal/hosting-a-reactjs-app-with-routing-on-aws-s3

// Set up the browser history with the updated location
// (minus the # sign)
const path = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
if (path) {
  window.location.hash = "";
  window.history.replaceState({}, "", path);
}

// Creating the root node for react
const container = document.getElementById("root");
if (!container) throw new Error("Root container not found");

const root = createRoot(container);

root.render(
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
            <React.StrictMode>
              <LoadingHOC>
                <ViewportProvider>
                  <ScrollToTop />
                  <App />
                </ViewportProvider>
              </LoadingHOC>
            </React.StrictMode>
          </Sentry.ErrorBoundary>
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>
  </Provider>
);
